import type { OrganiserVerification } from '@mmef/shared/api';

/**
 * What we actually know about who is running an event.
 *
 * <p>Shown on public listings on purpose. People decide from these pages whether to meet strangers
 * in person, and an organiser's name borrows real trust — so an unchecked name should look
 * different from one whose owner proved control of their ticketing account.
 *
 * <p>Never colour alone: each tier carries its own words. "Verified" with a green tick and
 * "Verified" with a grey one would be indistinguishable to plenty of readers, and meaningless to
 * anyone who doesn't know the convention.
 */
/*
 * Both labels describe where a listing came from. Neither says anything about whether an organiser
 * is trustworthy or an event is safe, and that restraint is deliberate: a badge implying we
 * examined someone is an assurance, and an assurance on a site where people go and meet strangers
 * is a promise we would have to be able to keep.
 *
 * "Connected their account" rather than "Verified organiser" for the same reason — the old wording
 * was doing more work than the underlying fact supports. What we know is that somebody signed in
 * to the account the events are published from. That is worth saying, and it is all we can say.
 */
const TIERS: Record<OrganiserVerification, { label: string; title: string; modifier: string }> = {
  PLATFORM_LINKED: {
    label: 'Connected their account',
    title:
      'This organiser signed in to the ticketing account these events are published from, so we know the listing came from them. We haven’t checked the organiser or the event itself.',
    modifier: 'badge--verified',
  },
  UNVERIFIED: {
    label: 'Unconfirmed listing',
    title:
      'Nobody has confirmed who runs this. Check the organiser’s own site before booking or travelling, and meet in public places.',
    modifier: 'badge--unverified',
  },
};

export function VerificationBadge({ tier }: { tier: OrganiserVerification | null }) {
  if (!tier) return null;
  const { label, title, modifier } = TIERS[tier];

  return (
    <span className={`badge ${modifier}`} title={title}>
      {label}
    </span>
  );
}
